
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { loadView, loadWidget, truncateContent } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { Getter, namespace } from 'vuex-class';
import moment from 'moment-timezone';
import {
  IAPIKeys,
  IPaginate,
  IScheduledEvent,
  ISenderID,
  IUser,
} from '@/types/types';
import { Watch } from 'vue-property-decorator';
const authModule = namespace('auth');
const apiKeyModule = namespace('apiKeys');
const smsModule = namespace('sms');
const eventsModule = namespace('events');
@Component({
  name: 'Dashboard',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    AddSMSSenderID: loadWidget(`widgets/AddSenderID`),
    EventCalendar: loadView(`pages/events/components/EventCalendar`),
    CreateEventDialog: loadView(`pages/events/dialogs/CreateEventDialog`),
    DeleteDialog: loadWidget('widgets/DeleteDialog'),
  },
  filters: {
    timestamp(value: string, format?: string) {
      return moment(value).format(format ?? 'Do MMMM, YYYY');
    },
    truncateContent,
  },
})
export default class Dashboard extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @Getter('getResetFormState') resetFormState!: boolean;
  @Getter('getWindowSize') windowSize!: number | null;
  @apiKeyModule.Getter('getAPIKeys') accessKeys!: IAPIKeys;

  @smsModule.Getter('getSenderIDList') smsIDList!: Array<
    ISenderID & { uuid: string; id: number; slug: string }
  >;

  @authModule.Getter('getUserDetails') user!: IUser;
  @eventsModule.Getter('getAllEvents') events!: Array<IScheduledEvent>;
  @eventsModule.Getter('getEventDetails') eventDetails!: IScheduledEvent;
  @eventsModule.Getter('getAllPaginatedEvents')
  paginatedEventList!: Array<IScheduledEvent>;
  @eventsModule.Getter('getPagination') paginate!: IPaginate & {
    limit: number;
  };

  date = [];
  menu = false;
  query = '';
  picker = '';
  times: string[] = [];
  timezones: string[] = [];
  currentDate = '';

  incomingDateToCreateEvent: string | null = null;
  eventFromParent: {
    eventId: number | null;
    date: string | null;
  } | null = null;
  currentPaginatedPage = 1;

  get eventList(): Array<IScheduledEvent> {
    return (this.events ?? []).filter((event) => Boolean(event));
  }

  get paginatedEvents(): Array<IScheduledEvent> {
    return (this.paginatedEventList ?? []).filter((event) => Boolean(event));
  }

  get isSmallScreen(): boolean {
    return (this.windowSize ?? window.innerWidth) <= 1355;
  }

  get displayedEvents(): Array<IScheduledEvent> {
    return this.isSmallScreen ? this.paginatedEvents : this.eventList;
  }

  addNewEvent(payload: any): void {
    console.log(payload);
    this.$store.dispatch('events/schedule', payload);
  }

  @Watch('paginate')
  onPaginationChange(paginate: IPaginate): void {
    this.currentPaginatedPage = paginate.page ?? 1;
  }

  @Watch('currentPaginatedPage')
  onCurrentPageChange(page: number): void {
    this.$store.dispatch('events/paginatedList', {
      query: `?page=${page}&size=${
        this.paginate?.limit ?? 30
      }&isPaginated=true`,
    });
  }

  @Watch('windowSize')
  onWindowSizeChange(width: number | null, previousWidth: number | null): void {
    if (!width || !previousWidth) {
      return;
    }

    const wasSmallScreen = previousWidth <= 1355;
    const isSmallScreen = width <= 1355;

    if (wasSmallScreen !== isSmallScreen) {
      this.loadEventsForScreen(width);
    }
  }

  deleteEvent(): void {
    if (this.$route.query?.eventId) {
      let payload = {
        id: this.$route.query?.eventId,
        loading: {
          idx: 'delete',
          state: false,
        },
        view: this.$route.query?.view,
        date: decodeURIComponent(this.$route.query?.date as string),
      };
      this.$store.dispatch('events/delete', payload);
    }
  }

  openEventDialogFromParent(): void {
    this.incomingDateToCreateEvent = new Date().toISOString();
  }

  loadEventsForScreen(width: number): void {
    if (width <= 1355) {
      this.$store.dispatch('events/paginatedList', {
        query: `?page=1&size=30&isPaginated=true`,
      });
      return;
    }

    this.$store.dispatch('events/list', {
      query: `?activeDate=${moment().format('YYYY-MM-DD')}&isPaginated=false`,
    });
  }

  created(): void {
    const windowWidth = window.innerWidth;

    this.currentDate = moment().format('YYYY-MM-DD');
    this.$store.dispatch('auth/me');
    this.picker = moment().format('YYYY-MM-DD');
    this.$store.dispatch('sms/listSenderID', { isApproved: true });
    this.$store.dispatch('updateWindowSize', windowWidth, { root: true });

    this.loadEventsForScreen(windowWidth);

    this.$store.dispatch('contact/list', {
      page: 1,
      limit: 30,
    });
    this.times = Array(24 * 12) // 288 intervals -- for a 24 hr period, a hr is 60 minutes
      .fill(0)
      .map((_time, index) =>
        moment()
          .startOf('day') // Ensures we start at 12:00 AM today
          .add(5 * index, 'minutes') // Cleaner way to increment
          .format('hh:mm a')
      );
    this.$store.dispatch('checkCreditBalance');
    if (this.$route.query?.actionType && this.$route.query?.eventId) {
      const id = this.$route.query.eventId as string;
      this.open('edit', id);
    }
    window.addEventListener('resize', () => {
      this.$store.dispatch('updateWindowSize', window.innerWidth, {
        root: true,
      });
    });
  }
}
